import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { PartyPopper, Heart, Calendar, MapPin, Loader2, Check, X, HelpCircle, ExternalLink } from "lucide-react";
import AIImageWithDisclaimer from "../components/AIImageWithDisclaimer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stable per-device fingerprint (anon, localStorage-only)
function getFingerprint() {
  let fp = localStorage.getItem("dg_fp");
  if (!fp) {
    const random = Math.random().toString(36).slice(2) + Date.now().toString(36);
    fp = `fp_${random}`;
    localStorage.setItem("dg_fp", fp);
  }
  return fp;
}

export default function RSVPPage() {
  const { inviteId } = useParams();
  const fp = useMemo(() => getFingerprint(), []);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpStatus, setRsvpStatus] = useState(() => localStorage.getItem(`rsvp_${inviteId}`) || null);
  const [guestName, setGuestName] = useState("");
  const [message, setMessage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [optInPhone, setOptInPhone] = useState("");
  const [optInChecked, setOptInChecked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/smart-invites/public/${inviteId}?fp=${encodeURIComponent(fp)}`);
        if (!r.ok) throw new Error("not found");
        setInvite(await r.json());
      } catch {
        setError("This invite couldn't be found. It may have expired.");
      }
      setLoading(false);
    })();
  }, [inviteId, fp]);

  const submit = async (status) => {
    setSubmitting(true);
    setError("");
    try {
      const body = { status, guest_name: guestName, message, fingerprint: fp };
      if (optInChecked && optInPhone.trim()) body.opt_in_phone = optInPhone.trim();
      const r = await fetch(`${API_URL}/api/smart-invites/public/${inviteId}/rsvp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      setRsvpStatus(status);
      localStorage.setItem(`rsvp_${inviteId}`, status);
      setShowForm(false);
    } catch { setError("Couldn't save your RSVP. Please try again."); }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a0a24] via-[#2a0f3f] to-[#1a0a24] flex items-center justify-center">
        <Loader2 size={36} className="text-[#E83E8C] animate-spin" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a0a24] to-[#2a0f3f] flex flex-col items-center justify-center p-6 text-center text-white">
        <X size={48} className="text-[#E83E8C] mb-3" />
        <p className="text-lg font-semibold">{error}</p>
        <Link to="/" className="mt-4 text-[#FF9F1C] text-sm underline">Back to DoozyGo</Link>
      </div>
    );
  }

  const rsvpLabels = {
    attending: { label: "You're attending 🎉", color: "from-emerald-500 to-teal-500" },
    maybe:     { label: "Marked as Maybe",     color: "from-amber-500 to-orange-500" },
    no:        { label: "Can't make it",       color: "from-rose-500 to-pink-500" },
  };

  // Defensive date formatter: renders a friendly date regardless of whether
  // backend returns "2026-04-25" or a full ISO datetime.
  const fmtEventDate = (raw) => {
    if (!raw) return "";
    // Keep short YYYY-MM-DD strings as-is for backward compat, but parse anything datetime-like
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const d = new Date(raw + "T00:00:00");
      if (!isNaN(d)) return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
      return raw;
    }
    const d = new Date(raw);
    return isNaN(d) ? raw : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a0a24] via-[#2a0f3f] to-[#0f0818] pb-16" data-testid="rsvp-page">
      {/* Hero invite image */}
      <div className="max-w-md mx-auto px-4 pt-6">
        {invite.image_base64 ? (
          <div className="rounded-3xl overflow-hidden shadow-2xl shadow-[#E83E8C]/20 border border-white/10">
            <AIImageWithDisclaimer
              src={`data:image/jpeg;base64,${invite.image_base64}`}
              alt={`${invite.occasion} invitation`}
              className="w-full block"
              data-testid="rsvp-invite-image"
            />
          </div>
        ) : (
          <div className="rounded-3xl aspect-[3/4] bg-gradient-to-br from-[#E83E8C]/30 to-[#8b5cf6]/30 flex flex-col items-center justify-center p-8 text-center border border-white/10">
            <Heart size={48} className="text-white mb-3" />
            <p className="text-xs text-white/50 uppercase tracking-wider">You're Invited To</p>
            <h2 className="text-2xl font-bold text-white mt-1">{invite.host_name}'s {invite.occasion}</h2>
            <p className="text-sm text-white/70 mt-2">{fmtEventDate(invite.event_date)} {invite.event_time}</p>
          </div>
        )}

        {/* Details */}
        <div className="mt-5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-white" data-testid="rsvp-details">
          <div className="flex items-center gap-2 text-[11px] text-[#FF9F1C] font-semibold uppercase tracking-wider">
            <Heart size={12} /> You're Invited
          </div>
          <h1 className="text-2xl font-bold mt-1 leading-tight">
            {invite.host_name}'s {invite.occasion}
          </h1>
          <div className="mt-4 space-y-2.5 text-sm">
            <div className="flex items-center gap-2 text-white/80">
              <Calendar size={16} className="text-[#E83E8C] flex-shrink-0" />
              <span>{fmtEventDate(invite.event_date)}{invite.event_time ? ` at ${invite.event_time}` : ""}</span>
            </div>
            {invite.venue && (
              <div className="flex items-start gap-2 text-white/80">
                <MapPin size={16} className="text-[#E83E8C] flex-shrink-0 mt-0.5" />
                <span>{invite.venue}</span>
              </div>
            )}
            {invite.personal_note && (
              <p className="mt-3 pt-3 border-t border-white/10 italic text-white/70">
                "{invite.personal_note}"
              </p>
            )}
          </div>
        </div>

        {/* RSVP */}
        <div className="mt-5" data-testid="rsvp-actions">
          {rsvpStatus ? (
            <div className="space-y-3">
              <div className={`p-4 rounded-2xl bg-gradient-to-r ${rsvpLabels[rsvpStatus].color} text-white text-center font-semibold shadow-lg`} data-testid="rsvp-confirmation">
                <Check size={18} className="inline mr-1" />
                {rsvpLabels[rsvpStatus].label}
              </div>
              <button
                onClick={() => { setRsvpStatus(null); localStorage.removeItem(`rsvp_${inviteId}`); }}
                className="w-full text-xs text-white/50 hover:text-white/80 py-2"
                data-testid="rsvp-change-btn"
              >
                Change my response
              </button>
            </div>
          ) : !showForm ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => submit("attending")}
                  disabled={submitting}
                  className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white font-semibold text-xs shadow-lg hover:shadow-emerald-500/30 transition-all disabled:opacity-50"
                  data-testid="rsvp-attending"
                >
                  <Check size={20} /> Attending
                </button>
                <button
                  onClick={() => submit("maybe")}
                  disabled={submitting}
                  className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white font-semibold text-xs shadow-lg hover:shadow-orange-500/30 transition-all disabled:opacity-50"
                  data-testid="rsvp-maybe"
                >
                  <HelpCircle size={20} /> Maybe
                </button>
                <button
                  onClick={() => submit("no")}
                  disabled={submitting}
                  className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-white/10 border border-white/20 text-white/80 font-semibold text-xs hover:bg-white/15 transition-all disabled:opacity-50"
                  data-testid="rsvp-no"
                >
                  <X size={20} /> Can't make it
                </button>
              </div>

              {/* Opt-in for DoozyGo monthly party-ideas drip */}
              <label className="flex items-start gap-2 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors select-none" data-testid="rsvp-optin-label">
                <input
                  type="checkbox"
                  checked={optInChecked}
                  onChange={e => setOptInChecked(e.target.checked)}
                  className="mt-0.5 accent-[#E83E8C]"
                  data-testid="rsvp-optin-checkbox"
                />
                <div className="flex-1 text-xs text-white/80">
                  <span className="font-semibold text-white">Get party ideas on WhatsApp</span>
                  <p className="text-[10px] text-white/50 mt-0.5">
                    Occasion-matched ideas, seasonal offers, early invites to DoozyGo launches. One message a month. Unsubscribe anytime.
                  </p>
                  {optInChecked && (
                    <input
                      type="tel"
                      value={optInPhone}
                      onChange={e => setOptInPhone(e.target.value)}
                      onClick={e => e.preventDefault()}
                      placeholder="Your WhatsApp number (e.g. 98765 43210)"
                      className="mt-2 w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 text-xs outline-none focus:border-[#E83E8C]"
                      data-testid="rsvp-optin-phone"
                    />
                  )}
                </div>
              </label>
            </div>
          ) : null}
          {error && <p className="mt-3 text-xs text-rose-400 text-center">{error}</p>}
        </div>

        {/* CTA for guests to book their own */}
        <div className="mt-8 p-5 rounded-2xl bg-gradient-to-br from-[#E83E8C]/20 to-[#FF9F1C]/10 border border-[#E83E8C]/30 text-center" data-testid="rsvp-book-own">
          <PartyPopper size={28} className="text-[#FF9F1C] mx-auto mb-2" />
          <p className="text-white font-semibold text-sm">Planning your own party?</p>
          <p className="text-white/60 text-xs mt-1">DoozyGo sets it up for you — balloons, lights, backdrop, everything.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-3 px-5 py-2 rounded-full bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-semibold text-xs hover:opacity-90 transition-opacity"
            data-testid="rsvp-explore-doozygo"
          >
            Explore DoozyGo <ExternalLink size={12} />
          </Link>
        </div>

        <p className="text-[10px] text-white/30 text-center mt-6">Powered by DoozyGo Smart Invites</p>
      </div>
    </div>
  );
}
